import type { MouseEvent } from "react";
import { useRef, useState } from "react";
import {
    Typography,
    Avatar,
    Box,
    Popover,
    Stack,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { FoodsIconButton, FoodsTags, OptionsPopover, WhiteBox } from "@foodpilot/foods";

import { Post, Topic } from "../../types/post.types";
import { ReactionType } from "../../types/reaction.types";
import { PostActions } from "./PostActions";
import { PostReactions } from "./PostReactions";
import { PostTypeAccent, PostTypeBadge } from "./PostTypeBadge";
import { FacebookStyleComments, FacebookStyleCommentsHandle } from "../Comments/FacebookStyleComments";
import { useTranslation } from "react-i18next";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { resolveSocialDisplayContent } from "../../utils/resolveSocialDisplayContent";
import { inferPostType, stripTypeHashtagFromContent, filterUserTopics } from "../../utils/postType";
import { SocialContentLanguageToggle } from "../SocialContentLanguageToggle";
import {
    resolveDefaultSocialContentLanguageMode,
    SocialContentLanguageMode,
} from "src/api/client-api/social/useSocialContentLanguagePreference";

interface PostCardProps {
    post: Post;
    onReact: (postId: number, type: ReactionType) => void;
    onShare: (postId: number) => void;
    onTopicClick?: (topic: Topic) => void;
    onEdit?: (post: Post) => void;
    onDelete?: (postId: number) => void;
    compact?: boolean;
}

const getVideoLinks = (metadata?: Record<string, any>) => {
    const links = Array.isArray(metadata?.links) ? metadata.links : [];

    return links.filter((link): link is string => {
        if (typeof link !== "string") return false;

        return /\.(mp4|webm|mov)(\?.*)?$/i.test(link) || /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(link);
    });
};

export const PostCard = ({
    post,
    onReact,
    onShare,
    onTopicClick,
    onEdit,
    onDelete,
    compact = false,
}: PostCardProps) => {
    const theme = useTheme();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const commentsRef = useRef<FacebookStyleCommentsHandle>(null);
    const { i18n, t } = useTranslation();
    const [postContentLanguageOverride, setPostContentLanguageOverride] = useState<SocialContentLanguageMode | null>(
        null,
    );
    const contentLanguageMode =
        postContentLanguageOverride ?? resolveDefaultSocialContentLanguageMode(i18n.language, post.originalLocale);
    const videoLinks = getVideoLinks(post.metadata);
    const imageAttachments = (post.attachments ?? []).filter((attachment) => attachment.type === "image");
    const documentAttachments = (post.attachments ?? []).filter((attachment) => attachment.type === "document");
    const postType = inferPostType(post.content, post.topics ?? []);
    const userTopics = filterUserTopics(post.topics ?? []);
    const displayedPostContent = stripTypeHashtagFromContent(
        resolveSocialDisplayContent(post, contentLanguageMode),
        postType,
    );
    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (onEdit) {
            onEdit(post);
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        setDeleteDialogOpen(false);
        if (onDelete) {
            onDelete(post.id);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <WhiteBox
            id={`social-post-${post.id}`}
            sx={{
                mb: 2,
                overflow: "hidden",
                border: `1px solid ${theme.custom.grey[400]}`,
                boxShadow: theme.custom.shadows.grey,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                    boxShadow: theme.custom.shadows.grey,
                    transform: "translateY(-1px)",
                },
            }}
        >
            <Stack direction="row" alignItems="stretch">
                <PostTypeAccent postType={postType} />
                <Stack flex={1} minWidth={0}>
                    <Stack sx={{ p: 2, pb: 1 }} gap={2}>
                        <Stack direction="row" alignItems="flex-start" gap={2}>
                            <Avatar
                                variant="medium"
                                src={post.author.avatarUrl}
                                sx={{
                                    width: compact ? theme.spacing(4) : theme.spacing(5),
                                    height: compact ? theme.spacing(4) : theme.spacing(5),
                                    boxShadow: `0 0 0 3px ${theme.palette.primary[100]}`,
                                }}
                            >
                                {post.author.firstname?.[0]}
                                {post.author.lastname?.[0]}
                            </Avatar>

                            <Stack flex={1} gap={0.5} minWidth={0}>
                                <Typography variant="body-bold">
                                    {post.author.firstname} {post.author.lastname}
                                </Typography>
                                {!!post.author.organizationNames?.length && (
                                    <Typography variant="caption" color="text.secondary">
                                        {post.author.organizationNames.join(", ")}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {formatRelativeTime(
                                        new Date(post.createdAt),
                                        i18n.resolvedLanguage ?? i18n.language,
                                    )}
                                </Typography>
                            </Stack>

                            <Stack direction="row" alignItems="center" gap={1}>
                                <PostTypeBadge postType={postType} />
                                {(post.canModify || post.canDelete) && (
                                    <FoodsIconButton
                                        size="xs"
                                        icon="details"
                                        tooltip={{ text: t("social.post.more_actions", "More actions") }}
                                        buttonProps={{ onClick: handleMenuClick }}
                                    />
                                )}
                            </Stack>
                        </Stack>

                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {displayedPostContent}
                        </Typography>
                        <SocialContentLanguageToggle
                            content={post}
                            mode={contentLanguageMode}
                            onModeChange={setPostContentLanguageOverride}
                        />

                        {/* Community topics (excludes post-type system hashtags) */}
                        {userTopics.length > 0 && (
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                {userTopics.map((topic) => (
                                    <FoodsTags
                                        key={topic.id}
                                        label={`#${topic.name}`}
                                        startIcon="tag"
                                        onClick={() => onTopicClick?.(topic)}
                                    />
                                ))}
                            </Stack>
                        )}

                        {/* Attachments */}
                        {imageAttachments.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                {imageAttachments.length === 1 ?
                                    <Box
                                        component="img"
                                        src={imageAttachments[0].url}
                                        alt={imageAttachments[0].fileName}
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: 400,
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            objectFit: "cover",
                                            transition: "transform 0.2s ease",
                                            "&:hover": {
                                                transform: "scale(1.01)",
                                            },
                                        }}
                                        onClick={() => window.open(imageAttachments[0]?.url, "_blank")}
                                    />
                                :   <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                imageAttachments.length === 2 ?
                                                    "1fr 1fr"
                                                :   "repeat(auto-fit, minmax(150px, 1fr))",
                                            gap: 1,
                                            maxHeight: 300,
                                        }}
                                    >
                                        {imageAttachments.slice(0, 4).map((attachment, index) => (
                                            <Box
                                                key={attachment.id}
                                                sx={{
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    borderRadius: 1,
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => window.open(attachment.url, "_blank")}
                                            >
                                                <Box
                                                    component="img"
                                                    src={attachment.url}
                                                    alt={attachment.fileName}
                                                    sx={{
                                                        width: "100%",
                                                        height: imageAttachments.length === 2 ? 200 : 150,
                                                        objectFit: "cover",
                                                    }}
                                                />
                                                {index === 3 && imageAttachments.length > 4 && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor: "rgba(0,0,0,0.6)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "white",
                                                            fontSize: "1.2rem",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        +{imageAttachments.length - 4}
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                }
                            </Box>
                        )}

                        {documentAttachments.length > 0 && (
                            <Stack spacing={1} sx={{ mb: 2 }}>
                                {documentAttachments.map((attachment) => (
                                    <FoodsTags
                                        key={attachment.id}
                                        label={attachment.fileName}
                                        startIcon="documentSpace"
                                        onClick={() => window.open(attachment.url, "_blank")}
                                    />
                                ))}
                            </Stack>
                        )}

                        {videoLinks.length > 0 && (
                            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                                {videoLinks.map((link) => (
                                    <Button
                                        key={link}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => window.open(link, "_blank")}
                                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                                    >
                                        {t("social.post.open_video", "Open video")} · {link}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Stack>

                    <Stack sx={{ pt: 0, pb: 2, px: 2 }} alignItems="stretch">
                        {/* Reactions Summary */}
                        {post.reactionCount > 0 && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <PostReactions
                                    reactions={post.reactionsSummary}
                                    userReaction={post.userReaction}
                                    onReact={(type: any) => onReact(post.id, type)}
                                    compact
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {post.reactionCount}{" "}
                                    {post.reactionCount === 1 ?
                                        t("social.post.reaction", "reaction")
                                    :   t("social.post.reactions", "reactions")}
                                </Typography>
                                {post.commentCount > 0 && (
                                    <>
                                        <Box
                                            sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius: "50%",
                                                backgroundColor: theme.custom.grey[800],
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {post.commentCount}{" "}
                                            {post.commentCount === 1 ?
                                                t("social.post.comment", "comment")
                                            :   t("social.post.comments", "comments")}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <PostActions
                            post={post}
                            onReact={(type: any) => onReact(post.id, type)}
                            onComment={() => commentsRef.current?.focusCommentInput()}
                            onShare={() => onShare(post.id)}
                        />
                    </Stack>

                    {/* Facebook-style inline comments */}
                    <FacebookStyleComments ref={commentsRef} post={post} contentLanguageMode={contentLanguageMode} />
                </Stack>
            </Stack>

            {/* Options Menu */}
            <Popover
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <OptionsPopover
                    content={[
                        {
                            options: [
                                {
                                    id: "edit",
                                    label: t("social.post.edit", "Edit post"),
                                    disabled: !post.canModify || !onEdit,
                                    onClick: () => {
                                        handleEdit();
                                        return true;
                                    },
                                },
                                {
                                    id: "delete",
                                    label: t("social.post.delete", "Delete post"),
                                    disabled: !post.canDelete || !onDelete,
                                    onClick: () => {
                                        handleDeleteClick();
                                        return true;
                                    },
                                },
                            ],
                        },
                    ]}
                />
            </Popover>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle>{t("social.post.delete", "Delete Post")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t(
                            "social.post.delete_confirmation",
                            "Are you sure you want to delete this post? This action cannot be undone.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>{t("social.post.cancel", "Cancel")}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t("social.post.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </WhiteBox>
    );
};
